import type {
  IndexedCard,
  OpIndex,
  ComparisonOp,
  FieldFilterNode,
  HasFilterNode,
  QueryNode,
} from "@punkrecords/types";

function matchesComparison(op: ComparisonOp | undefined, lhs: number | null, rhsRaw: string): boolean {
  if (!op) return lhs !== null && `${lhs}` === rhsRaw;
  if (lhs === null) return false;
  const rhs = Number.parseFloat(rhsRaw);
  if (!Number.isFinite(rhs)) return false;
  switch (op) {
    case "<":
      return lhs < rhs;
    case "<=":
      return lhs <= rhs;
    case "=":
      return lhs === rhs;
    case ">":
      return lhs > rhs;
    case ">=":
      return lhs >= rhs;
    case "!=":
      return lhs !== rhs;
    default:
      return false;
  }
}

export function normalizeColorToken(value: string): string[] {
  const v = value.toLowerCase();
  if (!v) return [];
  if (["w", "white"].includes(v)) return ["White"];
  if (["u", "blue"].includes(v)) return ["Blue"];
  if (["b", "black"].includes(v)) return ["Black"];
  if (["r", "red"].includes(v)) return ["Red"];
  if (["g", "green"].includes(v)) return ["Green"];
  if (["y", "yellow"].includes(v)) return ["Yellow"];
  if (["p", "purple"].includes(v)) return ["Purple"];
  return [value];
}

function normalizeRarityToken(value: string): string {
  const v = value.toLowerCase();
  if (v === "promo" || v === "promotional") return "p";
  if (v === "common") return "c";
  if (v === "uncommon") return "uc";
  if (v === "rare") return "r";
  if (v === "super" || v === "superrare") return "sr";
  if (v === "secret" || v === "secretrare") return "sec";
  if (v === "leader") return "l";
  if (v === "treasure") return "tr";
  if (v === "sp" || v === "special" || v === "spcard" || v === "sp card") return "sp card";
  return v;
}

export function matchesField(card: IndexedCard, node: FieldFilterNode | HasFilterNode): boolean {
  if (node.kind === "has") {
    if (node.field === "trigger") {
      return !!card.trigger && card.trigger.toLowerCase() !== "n/a";
    }
    if (node.field === "counter") {
      if (card.counter !== null && card.counter > 0) return true;
      return /\[Counter\]/i.test(card.text ?? "");
    }
    return false;
  }

  const value = node.value.toLowerCase();
  switch (node.field) {
    case "set":
      return card.setCode.toLowerCase() === value;
    case "rarity": {
      const rarity = (card.rarity || "").toLowerCase();
      const rv = normalizeRarityToken(value);
      return rarity === rv;
    }
    case "type":
      return (card.class || "").toLowerCase().includes(value);
    case "text":
      return (card.text || "").toLowerCase().includes(value);
    case "name":
    case "free":
      return (card.name || "").toLowerCase().includes(value);
    case "id": {
      const id = (card.id || "").toLowerCase();
      const number = (card.number || "").toLowerCase();
      return id === value || number === value;
    }
    case "color": {
      const wanted = normalizeColorToken(node.value);
      if (!wanted.length) return true;
      const cardColors: string[] = card.colors || [];
      return wanted.every((w) =>
        cardColors.some((c: string) => c.toLowerCase() === w.toLowerCase()),
      );
    }
    case "cost":
      return matchesComparison(node.op, card.cost, node.value);
    case "pow":
      return matchesComparison(node.op, card.power, node.value);
    case "feature": {
      const feats: string[] = card.features || [];
      return feats.some((f: string) => f.toLowerCase().includes(value));
    }
    case "keyword": {
      const kws: string[] = card.keywords || [];
      if (kws.some((k: string) => k.toLowerCase().includes(value))) return true;
      const feats: string[] = card.features || [];
      return feats.some((f: string) => f.toLowerCase().includes(value));
    }
    case "art": {
      const id = (card.id || "");
      const isAlternate = /_p\d+$/i.test(id);

      if (value === "alternate") return isAlternate;
      if (value === "base" || value === "unique") return !isAlternate;
      if (value === "manga") return false;
      return false;
    }
    case "counter":
      return matchesComparison(node.op, card.counter, node.value);
    case "trigger": {
      return (card.trigger || "").toLowerCase().includes(value);
    }
    case "attribute": {
      const attrs: string[] = card.attributes || [];
      return attrs.some((a: string) => a.toLowerCase().includes(value));
    }
    case "blockicon": {
      const raw = card.blockIcon;
      const num = raw ? Number.parseInt(raw, 10) : null;
      const parsed = Number.isFinite(num) ? num : null;
      return matchesComparison(node.op, parsed, node.value);
    }
    default:
      return false;
  }
}

export function matchCard(card: IndexedCard, node: QueryNode): boolean {
  switch (node.kind) {
    case "field":
    case "has":
      return matchesField(card, node);
    case "not":
      return !matchCard(card, node.node);
    case "and":
      return node.nodes.every((n) => matchCard(card, n));
    case "or":
      return node.nodes.some((n) => matchCard(card, n));
    default:
      return false;
  }
}

export function searchLibrary(ast: QueryNode | null, index: OpIndex): IndexedCard[] {
  if (!ast) return [];
  return index.allCards.filter((card: IndexedCard) => matchCard(card, ast));
}
